import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    dot: 'bg-amber-400',
    icon: 'Clock',
    barColor: '#F59E0B',
  },
  approved: {
    label: 'Approved',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    dot: 'bg-blue-400',
    icon: 'CheckCircle',
    barColor: '#3B82F6',
  },
  processing: {
    label: 'Processing',
    color: 'text-violet-400',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/20',
    dot: 'bg-violet-400',
    icon: 'RefreshCw',
    barColor: '#8B5CF6',
  },
  paid: {
    label: 'Paid',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    dot: 'bg-emerald-400',
    icon: 'BadgeCheck',
    barColor: '#10B981',
  },
  rejected: {
    label: 'Rejected',
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
    dot: 'bg-red-400',
    icon: 'XCircle',
    barColor: '#EF4444',
  },
};

const EarningsBreakdown = ({ transactions, loading }) => {
  const [activeStatus, setActiveStatus] = useState('all');

  const grouped = transactions?.reduce((acc, t) => {
    const s = t?.status || 'pending';
    if (!acc?.[s]) acc[s] = { count: 0, total: 0, items: [] };
    acc[s].count += 1;
    acc[s].total += parseFloat(t?.amount || 0);
    acc?.[s]?.items?.push(t);
    return acc;
  }, {});

  const totalAll = transactions?.reduce((s, t) => s + parseFloat(t?.amount || 0), 0);

  const statusKeys = Object.keys(grouped || {});

  const filtered = activeStatus === 'all'
    ? transactions
    : transactions?.filter(t => t?.status === activeStatus);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
            <Icon name="PieChart" size={18} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground font-heading">Earnings Breakdown</h3>
            <p className="text-xs text-muted-foreground">By transaction status</p>
          </div>
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {['pending', 'approved', 'paid', 'rejected']?.map(status => {
          const cfg = STATUS_CONFIG?.[status];
          const data = grouped?.[status] || { count: 0, total: 0 };
          const pct = totalAll > 0 ? (data?.total / totalAll) * 100 : 0;
          return (
            <button
              key={status}
              onClick={() => setActiveStatus(activeStatus === status ? 'all' : status)}
              className={`p-3 rounded-lg border transition-all duration-200 text-left ${
                activeStatus === status
                  ? `${cfg?.bg} ${cfg?.border} ring-1 ring-inset ${cfg?.border}`
                  : 'bg-muted/30 border-border hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className={`w-2 h-2 rounded-full ${cfg?.dot}`} />
                <span className={`text-xs font-medium ${cfg?.color}`}>{cfg?.label}</span>
              </div>
              <p className="text-base font-bold text-foreground data-text">${data?.total?.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">{data?.count} txns · {pct?.toFixed(0)}%</p>
              {/* Mini bar */}
              <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, background: cfg?.barColor }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Transaction List */}
      <div className="px-4 pb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium text-muted-foreground">
            {activeStatus === 'all' ? 'All transactions' : `${STATUS_CONFIG?.[activeStatus]?.label} transactions`}
            <span className="ml-1 text-foreground">({filtered?.length || 0})</span>
          </p>
          {activeStatus !== 'all' && (
            <button
              onClick={() => setActiveStatus('all')}
              className="text-xs text-primary hover:text-primary/80 transition-colors"
            >
              Show all
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-6 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered?.length === 0 ? (
          <div className="py-6 text-center">
            <Icon name="Receipt" size={24} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No transactions found</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
            {filtered?.slice(0, 20)?.map((t, i) => {
              const cfg = STATUS_CONFIG?.[t?.status] || STATUS_CONFIG?.pending;
              return (
                <div key={t?.id || i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${cfg?.bg}`}>
                      <Icon name={cfg?.icon} size={12} className={cfg?.color} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{t?.campaignName || 'Campaign'}</p>
                      <p className="text-xs text-muted-foreground">{t?.date || ''}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="text-xs font-bold text-foreground data-text">${parseFloat(t?.amount || 0)?.toFixed(2)}</p>
                    <span className={`text-xs ${cfg?.color}`}>{cfg?.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EarningsBreakdown;
